import React, { useMemo } from 'react';

import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import { DeviceSharingRequest } from 'src/types/devices';
import { OperationStatus } from 'src/types';
import ApiError from 'src/types/error';
import {
  getExpiresAt,
  isAccessExpired
} from 'src/utils/format-access-remaining';
import AccessRemainingCell from '../shared-table-layout/access-remaining-cell';

import ActionMenu from './action-menu';
import DeviceImageCell from '../shared-table-layout/device-image-cell';
import SharedTableLayout from '../shared-table-layout';
import { useSharedTableState } from 'src/hooks/useSharedTableState';
import { useSharedTableColumns } from 'src/hooks/useSharedTableColumns';
import { useSharedTableSorting } from 'src/hooks/useSharedTableSorting';

const SHARED_WITH_ME_SORTABLE_COLUMNS = [
  'deviceName',
  'serialNumber',
  'accessRemaining'
];

export type SharedWithMeTableProps = {
  devices: DeviceSharingRequest[] | null;
  status: OperationStatus;
  error: ApiError | null;
  onRetry: () => void;
};

const SharedWithMeTable: React.FC<SharedWithMeTableProps> = (
  props: SharedWithMeTableProps
) => {
  const { devices, status, error, onRetry } = props;
  const { translate } = useDependencyManagerContext();
  const { isLoading, isErrored, noDeviceName, unknownDeviceName } =
    useSharedTableState(status);

  const { sortedDevices, sortBy, handleSort } = useSharedTableSorting(
    devices,
    noDeviceName
  );

  const columns = useSharedTableColumns(
    'shared-with-me-table',
    [],
    SHARED_WITH_ME_SORTABLE_COLUMNS
  );

  const tableData = useMemo(() => {
    if (!sortedDevices) return [];
    return sortedDevices.map((request) => ({
      id: request.id,
      deviceName: request.device.device_family ?? noDeviceName,
      deviceImage: (
        <DeviceImageCell
          imageUrl={request.device.device_image_url}
          imageAlt={translate(
            'components.shared-with-me-table.device-image-alt',
            {
              deviceFamily: request.device.device_family ?? unknownDeviceName,
              serialNumber: request.device.device_serial_nr
            }
          )}
          placeholderAriaLabel={translate(
            'components.shared-with-me-table.device-image-not-available-aria-label',
            {
              deviceFamily: request.device.device_family ?? unknownDeviceName
            }
          )}
        />
      ),
      serialNumber: request.device.device_serial_nr,
      accessRemaining: (
        <AccessRemainingCell expiresAt={getExpiresAt(request.policies)} />
      ),
      action: (
        <ActionMenu
          deviceId={request.id}
          device={request.device}
          isExpired={isAccessExpired(getExpiresAt(request.policies) ?? '')}
        />
      )
    }));
  }, [sortedDevices, translate, noDeviceName, unknownDeviceName]);

  return (
    <SharedTableLayout
      testIdPrefix="shared-with-me-table"
      title={translate('components.shared-with-me-table.title')}
      emptyMessage={translate('components.shared-with-me-table.empty')}
      error={error}
      isErrored={isErrored}
      isLoading={isLoading}
      onRetry={onRetry}
      columns={columns}
      tableData={tableData}
      sortBy={sortBy}
      onSort={handleSort}
    />
  );
};

export default SharedWithMeTable;
